
import emailjs from "emailjs-com";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FileText, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "./lib/supabaseClient.ts";

const STATUS_OPTIONS = ["en_attente", "confirmé", "livré"];

const statusColors = {
  en_attente: "bg-gray-100 text-gray-700",
  confirmé: "bg-yellow-100 text-yellow-700",
  livré: "bg-green-100 text-green-700",
};

const ClientOrdersTable = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("commandes_clients")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) console.error("Erreur lors du chargement des commandes:", error);
    else setOrders(data || []);
    console.log(data)
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm("Voulez-vous vraiment supprimer cette commande ?")) return;

    const { error } = await supabase
      .from("commandes_clients")
      .delete()
      .eq("id", orderId);

    if (error) {
      console.error("Erreur lors de la suppression:", error);
      alert("Échec de la suppression de la commande.");
    } else {
      setOrders((prev) => prev.filter((order) => order.id !== orderId));
      alert("Commande supprimée avec succès !");
    }
  };
  const formatPriceDT = (value) => {
    const num = Number(value);
    if (isNaN(num)) return value;
    return `${num.toLocaleString('fr-FR', {
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2  
    })} DT`;
  };
  const handleStatusChange = async (orderId, newStatus) => {
    // Update status locally
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId ? { ...order, statut: newStatus } : order
      )
    );

    // Update status in Supabase
    const { error } = await supabase
      .from("commandes_clients")
      .update({ statut: newStatus })
      .eq("id", orderId);

    if (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      return;
    }

     //  Only send email when confirmed
    if (newStatus === "confirmé") {
      const confirmedOrder = orders.find((order) => order.id === orderId);
      if (!confirmedOrder) return;

      const cart = confirmedOrder.items || [];
      const shippingCost = 8;

       //  Normalize millimes → dinars
      const normalizePrice = (price) => (price > 1000 ? price / 1000 : price);

       //  Calculate total excluding delivery
      const totalSansLivraison = cart.reduce(
        (acc, item) => acc + normalizePrice(item.selectedPrice) * item.quantity,
        0
      );

       //  Format order items for email
      const ordersForEmail = cart.map((item) => ({
        image_url:
          item.image ||
          "https://aujdasturxpqlzqjbztf.supabase.co/storage/v1/object/public/animalia/no-image.png",
        name: item.name,
        units: item.quantity,
        price: (normalizePrice(item.selectedPrice) * item.quantity).toFixed(3),
        variations: [
          item.selectedGout ? `Goût: ${item.selectedGout}` : null,
          item.selectedWeight ? `Poids: ${item.selectedWeight}` : null,
        ]
          .filter(Boolean)
          .join(" • "), // always a string
      }));

       //  Client info block
      const clientInfo = `
      <div style="text-align:left; font-size:14px; color:#555; margin-top:16px;">
        <p><strong>Client :</strong> ${confirmedOrder.nom} ${confirmedOrder.prenom}</p>
        <p><strong>Adresse :</strong> ${confirmedOrder.adresse}, ${confirmedOrder.ville}, ${confirmedOrder.gouvernorat}, ${confirmedOrder.code_postal}</p>
        <p><strong>Téléphone :</strong> ${confirmedOrder.tel1}${confirmedOrder.tel2 ? " / " + confirmedOrder.tel2 : ""
        }</p>
        <p><strong>Email :</strong> ${confirmedOrder.email}</p>
      </div>
    `;

       //  Prepare email content
      const emailContent = {
        order_id: confirmedOrder.id,
        name: `${confirmedOrder.nom} ${confirmedOrder.prenom}`,
        email: confirmedOrder.email,
        cost: {
          shipping: shippingCost.toFixed(3),
          total: (totalSansLivraison + shippingCost).toFixed(3),
        },
        orders: ordersForEmail,
        client_info: clientInfo,
      };

       //  Send to client
      emailjs
        .send(
          "service_9tb3fmc",
          "template_ballxe2",
          emailContent,
          "h53ZUGcAo9qIWRJs6"
        )
        .then(
          (response) => {
            console.log("✅ E-mail envoyé au client :", response.status, response.text);
          },
          (error) => {
            console.error("❌ Erreur envoi client :", error);
          }
        );

       //  Send admin copy
      emailjs
        .send(
          "service_9tb3fmc",
          "template_ballxe2",
          { ...emailContent, email: "animaliaplus@gmail.com" },
          "h53ZUGcAo9qIWRJs6"
        )
        .then(
          (response) => {
            console.log("✅ E-mail envoyé à l’admin :", response.status, response.text);
          },
          (error) => {
            console.error("❌ Erreur envoi admin :", error);
          }
        );
    }
  };

  const getSelectClasses = (status) => {
    const base =
      "w-full rounded px-2 py-1 text-xs font-semibold border focus:outline-none focus:ring-2";
    const colorClass = statusColors[status] || "bg-gray-100 text-gray-700";
    return `${base} ${colorClass} focus:ring-blue-500`;
  };

  const generateInvoicePDF = async (order) => {
    const doc = new jsPDF();

    const companyName = "Animalia Plus ";
    const companyAddress = "127 avenue Jugurtha, Mutuelleville, 1082 Tunis, Tunisie";
    const companyEmail = "animaliaplus@gmail.com";
    const companyPhone = "+216 90 117 027";
    const logoUrl = "https://i.postimg.cc/2yBdh9JJ/animalia.png"; // Your logo URL

    try {
       //  Fetch and convert logo to Base64
      const logoData = await fetch(logoUrl)
        .then((res) => res.blob())
        .then(
          (blob) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            })
        );

      // ---- HEADER ----
      doc.setFillColor(255, 230, 245);
      doc.rect(0, 0, 210, 30, "F");
      doc.addImage(logoData, "PNG", 14, 5, 25, 20);  //  Safe base64 version
      doc.setFontSize(22);
      doc.setTextColor(179, 0, 89);
      doc.text(companyName, 44, 17);
      doc.setFontSize(11);
      doc.setTextColor(60);
      doc.text(companyAddress, 44, 22);
      doc.text(`${companyEmail} | ${companyPhone}`, 44, 27);

      // ---- INVOICE TITLE ----
      doc.setFontSize(16);
      doc.setTextColor(40);
      doc.text("Facture Client", 14, 42);
      doc.setFontSize(11);
      doc.text(`Numéro de commande: ${order.id}`, 14, 48);
      doc.text(`Date: ${new Date(order.created_at).toLocaleDateString()}`, 14, 53);

      // ---- CLIENT INFO ----
      doc.setFontSize(13);
      doc.text("Informations Client:", 14, 63);
      doc.setFontSize(11);
      doc.text(`${order.nom} ${order.prenom}`, 14, 70);
      doc.text(`${order.adresse}`, 14, 75);
      doc.text(`${order.ville}, ${order.gouvernorat}, ${order.code_postal}`, 14, 80);
      doc.text(`Tel: ${order.tel1}${order.tel2 ? " / " + order.tel2 : ""}`, 14, 85);
      doc.text(`Email: ${order.email}`, 14, 90);

      // ---- ORDER ITEMS ----
      autoTable(doc, {
        startY: 100,
        head: [["Article", "Quantité", "Prix Unitaire (DT)", "Total (DT)"]],
        body: order.items.map((i) => {
          const unitPrice = i.selectedPrice > 1000 ? i.selectedPrice / 1000 : i.selectedPrice;
          const totalPrice = i.total > 1000 ? i.total / 1000 : i.total;

          // Construct variations string
          let variations = "";
          if (i.selectedGout) variations += `Goût : ${i.selectedGout}`;
          if (i.selectedGout && i.selectedWeight) variations += " • ";
          if (i.selectedWeight) variations += `Poids : ${i.selectedWeight}`;

          return [
            `${i.name}${variations ? `\n(${variations})` : ""}`, // add variations here
            i.quantity,
            unitPrice.toFixed(2),
            totalPrice.toFixed(2),
          ];
        }),
        headStyles: {
          fillColor: [255, 65, 180],
          textColor: 255,
          fontStyle: "bold",
        },
        bodyStyles: { textColor: 60 },
        alternateRowStyles: { fillColor: [255, 245, 250] },
      });


      // ---- TOTALS ----
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(12);
      doc.setTextColor(40);
      doc.text(`Sous-total : ${order.sous_total.toFixed(2)} DT`, 130, finalY);
      doc.text(`Frais de livraison : ${order.frais_livraison.toFixed(2)} DT`, 130, finalY + 6);
      doc.setFontSize(14);
      doc.setTextColor(0, 128, 0);
      doc.text(`Total : ${order.total.toFixed(2)} DT`, 130, finalY + 14);

      // ---- FOOTER ----
      const footerY = 285;
      doc.setDrawColor(255, 65, 180);
      doc.line(14, footerY - 5, 196, footerY - 5);
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text("Merci pour votre commande 💚", 14, footerY);
      doc.text("www.animalia.tn", 170, footerY, { align: "right" });

      // ---- SAVE ----
      doc.save(`Facture_${order.id}.pdf`);
    } catch (error) {
      console.error("Erreur génération PDF:", error);
      alert("Erreur lors de la génération du PDF (logo invalide ou inaccessible).");
    }
  };
  const renderVariation = (item) => {
    if (!item.selectedGout && !item.selectedWeight) return null;

    return (
      <div
        style={{
          fontSize: "12px",
          color: "#777",
          marginTop: "2px",
          marginBottom: "4px",
        }}
      >
        {item.selectedGout && <span>Goût : {item.selectedGout}</span>}
        {item.selectedGout && item.selectedWeight && <span> • </span>}
        {item.selectedWeight && <span>Poids : {item.selectedWeight}</span>}
      </div>
    );
  };

  if (loading) return <p>Chargement des commandes...</p>;

  return (
    <div
      style={{
        padding: "50px",
        maxWidth: "1200px",
        margin: "0 auto",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          marginBottom: 30,
          color: "#333",
          fontWeight: "700",
          fontSize: "2rem",
        }}
      >
        📦 Commandes Clients
      </h2>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: "0 12px",
            minWidth: "900px",
          }}
        >
          <thead>
            <tr>
              <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
                Informations client
              </th>
              <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
                Articles
              </th>
              <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
                Frais Livraison
              </th>
              <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
                Total
              </th>
              <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
                Statut
              </th>
              <th style={{ backgroundColor: "#ffe6f0", color: "#b30059" }}>
                Facture / Suppr
              </th>
            </tr>
          </thead>

          <tbody>
            {orders.map((order) => (

              <tr
                key={order.id}
                style={{
                  backgroundColor: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  transition: "background-color 0.3s ease",
                }}
              >
                {/* Client Info */}
                <td style={{ padding: "14px 18px" }}>
                  <div>
                    <b>
                      {order.nom} {order.prenom}
                    </b>
                  </div>
                  <div style={{ color: "#b30059" }}>{order.adresse}</div>
                  <div>
                    {order.ville}, {order.gouvernorat}, {order.code_postal}
                  </div>
                  <div style={{ color: "#555" }}>
                    {order.tel1}
                    {order.tel2 ? ` / ${order.tel2}` : ""}
                  </div>
                  <div style={{ color: "#b30059", fontWeight: "600" }}>
                    {order.email}
                  </div>
                </td>

                {/* Articles */}
                <td style={{ padding: "14px 18px" }}>
                  {order.items?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {order.items.map((item, i) => (

                        <div
                          key={i}
                          style={{
                            border: "1px solid #eee",
                            borderRadius: "8px",
                            padding: "10px",
                            backgroundColor: "#fff9fc",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              style={{
                                width: "60px",
                                height: "60px",
                                objectFit: "cover",
                                borderRadius: "8px",
                                border: "1px solid #ddd",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "60px",
                                height: "60px",
                                backgroundColor: "#f0f0f0",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#aaa",
                                fontSize: "12px",
                              }}
                            >
                              No Img
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: "600" }}>{item.name}
                            </div>
                            {renderVariation(item)}

                            <div style={{ fontSize: "14px", color: "#555" }}>
                              Qté: <b>{item.quantity}</b> × {item.selectedPrice} DT
                            </div>
                            <div
                              style={{
                                fontWeight: "700",
                                color: "green",
                                marginTop: "4px",
                              }}
                            >
                              <span>Total: {formatPriceDT(item.total)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: "#999" }}>Aucun article</span>
                  )}
                </td>

                <td style={{ padding: "14px 18px" }}>{order.frais_livraison} DT</td>

                <td style={{ padding: "14px 18px", color: "green", fontWeight: "700" }}>
                  <span> {formatPriceDT(order.total)}</span>
                </td>

                <td style={{ padding: "14px 18px" }}>
                  <select
                    value={order.statut}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    className={getSelectClasses(order.statut)}
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </td>

                {/* PDF + Delete */}
                <td style={{ padding: "14px 18px", display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => generateInvoicePDF(order)}
                    style={{
                      backgroundColor: "transparent",
                      color: "#007bff",
                      padding: "8px 8px",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Télécharger la facture"
                  >
                    <FileText size={18} />
                  </button>

                  <button
                    onClick={() => handleDeleteOrder(order.id)}
                    style={{
                      backgroundColor: "transparent",
                      color: "#e25b39ff",
                      padding: "8px 8px",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Supprimer la commande"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientOrdersTable;
